import * as React from 'react';
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { useForm } from 'react-hook-form';
import {
	publicClientEntitiesPageUrl,
	publicClientFamilyPageUrl,
	publicClientReviewPageUrl
} from '../../../core/constants';
import Tile from '../../common/Tile';
import Helmet from '../../../commonComponents/Helmet';
import PublicAutoLogoutLayout, {
	StyledFormDiv,
	StyledLabel,
	SubTitleParagraph,
	TitleParagraph
} from './common/PublicLayout';
import PublicPageSteps from './common/PublicPageSteps';
import { blackColour } from '../../cssConstants';
import PublicForm from './common/PublicForm';
import { personalInterests } from '../userProfile/formFieldsSourceData';
import CheckboxField from '../../../commonComponents/fields/CheckboxField';
import { useProgress, useRequireProgress } from './common/PublicProgressContext';
import {
	ctxTermsAccepted,
	ctxClientTypeSelected,
	ctxClientBasicInfoCompleted,
	individualClientType
} from './common/publicConstants';
import { getPublicClientInterests, updatePublicClientInterests } from '../../../core/api/publicClientApi';
import { PublicClientInterests } from '../../../models/PublicClientModels';
import Input from '../../../commonComponents/appForm/Input';
import useAsyncAction from '../../../commonComponents/Hooks';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';

const PublicInterestsPage = () => {
	const navigate = useNavigateWithAppName();
	const { clientTypeSelected, clientBasicInfoCompleted } = useProgress();
	const [currentStep] = useState<number>(4);
	const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
	const [showCustomInterest, setShowCustomInterest] = useState(false);
	const { setValue } = useForm({
		defaultValues: {
			otherInterests: ''
		}
	});
	const performAsyncAction = useAsyncAction();
	const { signal } = new AbortController();

	// Ensure the user has accepted the terms, selected a client type to access this page.
	useRequireProgress([ctxTermsAccepted, ctxClientTypeSelected, ctxClientBasicInfoCompleted]);

	useEffect(() => {
		if (clientBasicInfoCompleted) {
			performAsyncAction(async () => {
				const clientInterests = await getPublicClientInterests(signal);

				if (clientInterests) {
					const interestsArray = clientInterests.split(',').map((i) => i.trim());

					// Filter out the "Others" custom value and store the rest in selectedInterests
					const knownInterests = interestsArray.filter((i) => personalInterests.includes(i));
					setSelectedInterests(knownInterests);

					// Check if "Others" is part of the interests
					const othersInterest = interestsArray.filter((i) => !personalInterests.includes(i));
					if (othersInterest.length > 0) {
						setValue('otherInterests', othersInterest.join(', '));
						setShowCustomInterest(true);
					}
				}
			});
		}
	}, []);

	const handleBackButtonClick = () => {
		if (clientTypeSelected === individualClientType) {
			navigate(publicClientFamilyPageUrl);
		} else {
			navigate(publicClientEntitiesPageUrl);
		}
	};

	const handleSubmit = async (clientInterests: PublicClientInterests) => {
		await performAsyncAction(async () => {
			const interestsString = selectedInterests.join(', ');
			const finalInterests =
				interestsString && clientInterests.otherInterests
					? `${interestsString}, ${clientInterests.otherInterests}`
					: interestsString || clientInterests.otherInterests;

			const updateClientInterests = {
				...clientInterests,
				interests: finalInterests // Combined selected interests and otherInterests
			};
			await updatePublicClientInterests(updateClientInterests, signal);
			navigate(publicClientReviewPageUrl);
		});
	};

	const handleChange = (interest: string) => {
		if (interest === 'Others') {
			setShowCustomInterest((prev) => !prev);
		} else {
			setSelectedInterests((prevInterests) => {
				const updatedInterests = prevInterests.includes(interest)
					? prevInterests.filter((i) => i !== interest)
					: [...prevInterests, interest];

				return updatedInterests;
			});
		}
	};

	return (
		<PublicAutoLogoutLayout>
			<Helmet>
				<title>Join Kelly+Partners | Prospective Client</title>
			</Helmet>
			<Tile>
				<PublicPageSteps currentStep={currentStep} />
				<TitleParagraph>Personal Interests</TitleParagraph>
				<SubTitleParagraph>Please share your personal interests with us.</SubTitleParagraph>
				<PublicForm onSubmit={handleSubmit} onBackButtonClick={handleBackButtonClick}>
					<StyledFormDiv>
						<StyledLabel>Please tick your interests below.</StyledLabel>
						<div />
					</StyledFormDiv>
					<StyledFormDiv>
						<CheckboxContainer>
							{personalInterests.map((interest) => (
								<StyledCheckbox
									key={interest}
									name={interest}
									label={interest}
									checked={selectedInterests.includes(interest)}
									onCheckboxChange={() => handleChange(interest)}
								/>
							))}
							{/* Add the "Others" option */}
							<StyledCheckbox
								key="Others"
								name="Others"
								label="Other - not listed"
								checked={showCustomInterest}
								onCheckboxChange={() => handleChange('Others')}
							/>
							{showCustomInterest && <Input label="Other interests" name="otherInterests" maxLength={100} />}
						</CheckboxContainer>
						<div />
					</StyledFormDiv>
				</PublicForm>
			</Tile>
		</PublicAutoLogoutLayout>
	);
};

export default PublicInterestsPage;

const CheckboxContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledCheckbox = styled(CheckboxField)`
	margin: 5px 0;
	width: 100% !important;
	color: ${blackColour};

	.k-checkbox {
		margin-right: 10px;
	}
`;
